import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Fab,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import http from "../utils/http-common";

type Msg = { role: "user" | "assistant"; content: string };
type Session = { id: string; title: string; messages: Msg[] };

const STORAGE_KEY = "dso_assistant_sessions_v1";

const SUGGESTIONS = [
  "How many cases failed and why?",
  "What are the most recent non-passing cases?",
  "Summarize the status of my suites",
];

const greeting = (): Msg => ({
  role: "assistant",
  content:
    "Hi! I'm the **DeviceSure Assistant**. Ask me about your test cases and suites — statuses, what failed, what a case does, or what to try next.",
});

const newSession = (n = 1): Session => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: `Chat ${n}`,
  messages: [greeting()],
});

// tiny markdown-ish renderer: **bold**, `code`
const renderText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p))
      return (
        <code key={i} style={{ background: "rgba(0,0,0,0.06)", padding: "0 4px", borderRadius: 4 }}>
          {p.slice(1, -1)}
        </code>
      );
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const AssistantChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Session[]) : null;
      if (parsed && parsed.length) return parsed;
    } catch {
      /* ignore */
    }
    return [newSession(1)];
  });
  const [activeId, setActiveId] = useState<string>(() => "");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const active = useMemo(
    () => sessions.find((s) => s.id === activeId) || sessions[0],
    [sessions, activeId]
  );

  useEffect(() => {
    if (!sessions.find((s) => s.id === activeId)) setActiveId(sessions[0]?.id || "");
  }, [sessions, activeId]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch {
      /* ignore quota */
    }
  }, [sessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [active?.messages, loading, open, fullscreen]);

  const patchActive = (fn: (s: Session) => Session) =>
    setSessions((prev) => prev.map((s) => (s.id === active.id ? fn(s) : s)));

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || loading || !active) return;
    const isFirst = active.messages.filter((m) => m.role === "user").length === 0;
    const nextMsgs = [...active.messages, { role: "user" as const, content: q }];
    patchActive((s) => ({
      ...s,
      title: isFirst ? q.slice(0, 28) : s.title,
      messages: nextMsgs,
    }));
    setInput("");
    setLoading(true);
    try {
      const payload = nextMsgs.slice(1); // drop the local greeting
      const res = await http.post("/ai/chat", { messages: payload });
      const reply = res.data?.reply || "(no answer)";
      patchActive((s) => ({ ...s, messages: [...nextMsgs, { role: "assistant", content: reply }] }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || "AI request failed.";
      patchActive((s) => ({ ...s, messages: [...nextMsgs, { role: "assistant", content: `⚠️ ${msg}` }] }));
    } finally {
      setLoading(false);
    }
  };

  const addChat = () => {
    const s = newSession(sessions.length + 1);
    setSessions((prev) => [...prev, s]);
    setActiveId(s.id);
  };

  const closeChat = (id: string) => {
    setSessions((prev) => {
      const rest = prev.filter((s) => s.id !== id);
      return rest.length ? rest : [newSession(1)];
    });
  };

  const panelSx = fullscreen
    ? { top: 16, left: 16, right: 16, bottom: 16, width: "auto", height: "auto" }
    : {
        bottom: 24,
        right: 24,
        width: { xs: "calc(100vw - 32px)", sm: 420 },
        height: 600,
        maxHeight: "calc(100vh - 48px)",
      };

  return (
    <>
      {!open && (
        <Tooltip title="DeviceSure Assistant" placement="left">
          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}
          >
            <AutoAwesomeIcon />
          </Fab>
        </Tooltip>
      )}

      {open && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            borderRadius: fullscreen ? 2 : 3,
            overflow: "hidden",
            ...panelSx,
          }}
        >
          {/* header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 1.25, bgcolor: "primary.main", color: "#fff" }}
          >
            <AutoAwesomeIcon fontSize="small" />
            <Typography sx={{ fontWeight: 800, flexGrow: 1 }}>DeviceSure Assistant</Typography>
            <Tooltip title="New chat">
              <IconButton size="small" sx={{ color: "#fff" }} onClick={addChat}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton size="small" sx={{ color: "#fff" }} onClick={() => setFullscreen((f) => !f)}>
                {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" sx={{ color: "#fff" }} onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* chat tabs */}
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "#fff" }}>
            <Tabs
              value={active?.id || false}
              onChange={(_, v) => setActiveId(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              {sessions.map((s) => (
                <Tab
                  key={s.id}
                  value={s.id}
                  sx={{ minHeight: 40, textTransform: "none", pr: 0.5 }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</span>
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeChat(s.id);
                        }}
                        sx={{ display: "flex", opacity: 0.5, "&:hover": { opacity: 1 } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </Box>
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* messages */}
          <Box ref={scrollRef} sx={{ flex: 1, overflow: "auto", p: 1.5, bgcolor: "#f6f7f9" }}>
            <Stack spacing={1.25} sx={{ maxWidth: fullscreen ? 820 : "none", mx: fullscreen ? "auto" : 0 }}>
              {active?.messages.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "88%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    fontSize: "0.88rem",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    bgcolor: m.role === "user" ? "primary.main" : "#fff",
                    color: m.role === "user" ? "#fff" : "text.primary",
                    border: m.role === "user" ? "none" : "1px solid",
                    borderColor: "divider",
                    boxShadow: m.role === "user" ? "none" : "0 1px 4px rgba(16,24,40,0.06)",
                  }}
                >
                  {renderText(m.content)}
                </Box>
              ))}
              {loading && (
                <Box sx={{ alignSelf: "flex-start", px: 1.5, py: 1 }}>
                  <CircularProgress size={16} />
                </Box>
              )}
              {active && active.messages.length <= 1 && !loading && (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {SUGGESTIONS.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      variant="outlined"
                      onClick={() => send(s)}
                      sx={{ justifyContent: "flex-start", height: "auto", py: 0.5, "& .MuiChip-label": { whiteSpace: "normal" } }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>

          {/* input */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider", bgcolor: "#fff" }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about cases, suites, failures…"
              multiline
              maxRows={fullscreen ? 8 : 4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
            />
            <IconButton color="primary" onClick={() => send(input)} disabled={loading || !input.trim()}>
              <SendIcon />
            </IconButton>
          </Stack>
        </Paper>
      )}
    </>
  );
};

export default AssistantChat;
